import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  Building2,
  Link,
  Unlink,
  RefreshCw,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  CheckCircle,
  AlertCircle,
  CreditCard,
  Wallet
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const BankAccounts = ({ ownerId, ownerName, ownerType }) => {
  const { token, isManager } = useAuth();
  const [connections, setConnections] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [showPlaidLink, setShowPlaidLink] = useState(false);
  const [disconnectId, setDisconnectId] = useState(null);
  const [plaidReady, setPlaidReady] = useState(false);
  
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [connectionsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/plaid/connections/${ownerId}`, { headers }),
        axios.get(`${API}/plaid/transactions/${ownerId}`, { headers })
      ]);
      setConnections(connectionsRes.data || []);
      setTransactions(transactionsRes.data?.transactions || []);
      setSummary(transactionsRes.data?.summary || null);
    } catch (error) {
      console.error("Failed to fetch bank data:", error);
    } finally {
      setLoading(false);
    }
  }, [ownerId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load Plaid Link script
  useEffect(() => {
    if (document.getElementById('plaid-link-script')) {
      setPlaidReady(true);
      return;
    }
    
    const script = document.createElement('script');
    script.id = 'plaid-link-script';
    script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    script.async = true;
    script.onload = () => setPlaidReady(true);
    document.body.appendChild(script);
  }, []);

  const initiatePlaidLink = async () => {
    try {
      const res = await axios.post(`${API}/plaid/create-link-token`, { owner_id: ownerId }, { headers });
      setLinkToken(res.data.link_token);
      setShowPlaidLink(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initialize bank connection");
    }
  };

  useEffect(() => {
    if (linkToken && showPlaidLink && plaidReady && window.Plaid) {
      const handler = window.Plaid.create({
        token: linkToken,
        onSuccess: async (publicToken, metadata) => {
          try {
            await axios.post(`${API}/plaid/exchange-token`, {
              public_token: publicToken,
              owner_id: ownerId
            }, { headers });
            toast.success("Bank account connected successfully!");
            fetchData();
          } catch (error) {
            toast.error("Failed to connect bank account");
          }
          setShowPlaidLink(false);
          setLinkToken(null);
        },
        onExit: (err) => {
          if (err) console.error("Plaid Link error:", err);
          setShowPlaidLink(false);
          setLinkToken(null);
        }
      });
      handler.open();
    }
  }, [linkToken, showPlaidLink, plaidReady, ownerId]);

  const syncTransactions = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/plaid/sync-transactions`, { owner_id: ownerId }, { headers });
      toast.success(`Synced ${res.data.transactions_synced} transactions`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync transactions");
    } finally {
      setSyncing(false);
    }
  };

  const disconnectBank = async () => {
    try {
      await axios.delete(`${API}/plaid/connections/${disconnectId}`, { headers });
      toast.success("Bank account disconnected");
      setDisconnectId(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to disconnect bank");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(amount));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Bank Accounts</h3>
          <p className="text-sm text-slate-500">
            Connected accounts for {ownerName}
          </p>
        </div>
        <div className="flex gap-2">
          {connections.length > 0 && (
            <Button variant="outline" onClick={syncTransactions} disabled={syncing}>
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sync
            </Button>
          )}
          {isManager && (
            <Button onClick={initiatePlaidLink} className="bg-green-700 hover:bg-green-800">
              <Link className="w-4 h-4 mr-2" />
              Connect Bank
            </Button>
          )}
        </div>
      </div>

      {/* Connected Accounts */}
      {connections.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">No bank accounts connected</p>
            {isManager && (
              <Button onClick={initiatePlaidLink} variant="outline">
                <Link className="w-4 h-4 mr-2" />
                Connect your first bank account
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {connections.map(conn => (
            <Card key={conn.id}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">{conn.accounts?.[0]?.name || "Bank Account"}</p>
                      <p className="text-sm text-slate-500">
                        ****{conn.accounts?.[0]?.mask || "0000"} • 
                        Last sync: {conn.last_sync ? new Date(conn.last_sync).toLocaleDateString() : "Never"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {conn.accounts?.[0]?.current_balance != null && (
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Balance</p>
                        <p className="font-semibold">{formatCurrency(conn.accounts[0].current_balance)}</p>
                      </div>
                    )}
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Connected
                    </Badge>
                    {isManager && (
                      <Button variant="ghost" size="icon" onClick={() => setDisconnectId(conn.id)} className="text-red-600">
                        <Unlink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Summary */}
      {summary && summary.transaction_count > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <ArrowDownRight className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Income</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(summary.total_income)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <ArrowUpRight className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Expenses</p>
                  <p className="text-xl font-bold text-red-600">{formatCurrency(summary.total_expenses)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Net</p>
                  <p className={`text-xl font-bold ${summary.net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(summary.net)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Transactions */}
      {transactions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {transactions.slice(0, 10).map(txn => (
                <div key={txn.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      txn.transaction_type === 'rent_income' || txn.transaction_type === 'income'
                        ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {txn.transaction_type === 'rent_income' || txn.transaction_type === 'income' ? (
                        <ArrowDownRight className="w-4 h-4 text-green-600" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{txn.merchant_name || txn.name}</p>
                      <p className="text-xs text-slate-500">{txn.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {txn.auto_categorized && (
                      <Badge variant="outline" className="text-xs">Auto</Badge>
                    )}
                    <span className={`font-semibold ${
                      txn.transaction_type === 'rent_income' || txn.transaction_type === 'income'
                        ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {txn.transaction_type === 'rent_income' || txn.transaction_type === 'income' ? '+' : '-'}
                      {formatCurrency(txn.amount)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Disconnect Dialog */}
      <AlertDialog open={!!disconnectId} onOpenChange={() => setDisconnectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Bank Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the bank connection and all synced transactions. You can reconnect later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={disconnectBank} className="bg-red-600 hover:bg-red-700">
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BankAccounts;
